import { useState, useRef } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  FileJson,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import { safeText, safeNumber } from "@/lib/data-guard";

type ImportFormat = "json" | "csv" | "text";

interface ImportResultItem {
  recordId: string;
  instanceId: string;
  status: "SUCCESS" | "FAILED";
  error?: string;
}

export default function Import() {
  const [activeFormat, setActiveFormat] = useState<ImportFormat>("json");
  const [content, setContent] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [results, setResults] = useState<ImportResultItem[] | null>(null);
  const [summary, setSummary] = useState<{ total: number; imported: number; failed: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);


  const importJSON = trpc.intake.importJSON.useMutation();
  const importCSV = trpc.intake.importCSV.useMutation();
  const importText = trpc.intake.importText.useMutation();

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setContent(text);

      // Auto-detect format from file extension
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext === "json") setActiveFormat("json");
      else if (ext === "csv") setActiveFormat("csv");
      else setActiveFormat("text");

      toast.success(`File loaded: ${file.name}`, { description: `${text.length} characters` });
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!content.trim()) {
      toast.error("No content", { description: "Paste or upload content to import" });
      return;
    }

    setIsImporting(true);
    setResults(null);
    setSummary(null);

    try {
      let result: any;

      if (activeFormat === "json") {
        result = await importJSON.mutateAsync({ content, createdBy: "ui-import" });
      } else if (activeFormat === "csv") {
        result = await importCSV.mutateAsync({ content, createdBy: "ui-import" });
      } else {
        result = await importText.mutateAsync({ content, createdBy: "ui-import" });
      }

      setResults(result.results);
      setSummary({ total: result.totalRecords, imported: result.imported, failed: result.failed });

      if (result.imported > 0) {
        toast.success(`Import complete`, {
          description: `${result.imported} of ${result.totalRecords} records imported successfully`,
        });
      } else {
        toast.error("Import failed", {
          description: result.results[0]?.error || "No records could be imported",
        });
      }
    } catch (error) {
      toast.error("Import error", {
        description: String(error),
      });
    } finally {
      setIsImporting(false);
    }
  };

  const formatTabs: Array<{ id: ImportFormat; label: string; icon: React.ReactNode; desc: string }> = [
    { id: "json", label: "JSON", icon: <FileJson className="h-5 w-5" />, desc: "Luminari export or generic JSON" },
    { id: "csv", label: "CSV", icon: <FileSpreadsheet className="h-5 w-5" />, desc: "Tabular data with headers" },
    { id: "text", label: "Text", icon: <FileText className="h-5 w-5" />, desc: "Free-form narrative" },
  ];

  const placeholders: Record<ImportFormat, string> = {
    json: `Paste Luminari JSON export here, or any JSON with fields like:
{
  "problem_type": "DENIAL",
  "jurisdiction": "Washington",
  "system_primary": "Child Welfare",
  "description": "..."
}

Also accepts Luminari v2 export format for round-trip import.`,
    csv: `Paste CSV data here. Example:

problem_type,jurisdiction,system_primary,title,description
DENIAL,Washington,Child Welfare,Benefits Denial,Client denied TANF benefits...
GAP,California,Housing,Service Gap,No shelter beds available...`,
    text: `Paste narrative text here. The system will auto-detect:
- Problem type (denial, gap, contradiction, etc.)
- Jurisdiction (state, county, city)
- System involved (child welfare, housing, healthcare, etc.)

Example:
"My client in King County, Washington was denied TANF benefits despite meeting all eligibility requirements. The caseworker cited a prior overpayment from 2019 that has already been resolved. We have documentation showing the overpayment was repaid in full..."`,
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border/40 bg-card/50">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Link href="/control-room">
              <Button variant="ghost" size="icon" className="shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Import Data</h1>
              <p className="text-sm text-muted-foreground">
                Import problem instances from JSON, CSV, or text
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-8 space-y-6">
        {/* Format Selection */}
        <div className="grid grid-cols-3 gap-4">
          {formatTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFormat(tab.id)}
              className={`p-4 rounded-lg border text-left transition-all ${
                activeFormat === tab.id
                  ? "border-primary bg-primary/10 ring-1 ring-primary"
                  : "border-border/40 bg-card hover:border-border"
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={activeFormat === tab.id ? "text-primary" : "text-muted-foreground"}>
                  {tab.icon}
                </div>
                <span className="font-semibold">{tab.label}</span>
              </div>
              <p className="text-xs text-muted-foreground">{tab.desc}</p>
            </button>
          ))}
        </div>

        {/* File Upload */}
        <Card className="border-border/40 bg-card/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.csv,.txt,.text"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                Upload File
              </Button>
              <span className="text-sm text-muted-foreground">
                or paste content below
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Content Input */}
        <Card className="border-border/40 bg-card/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {formatTabs.find((t) => t.id === activeFormat)?.icon}
              {activeFormat.toUpperCase()} Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={placeholders[activeFormat]}
              className="min-h-[300px] font-mono text-sm bg-background"
            />
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-muted-foreground">
                {content.length > 0 ? `${content.length} characters` : "No content"}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setContent(""); setResults(null); setSummary(null); }}
                  disabled={!content && !results}
                >
                  Clear
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={isImporting || !content.trim()}
                  className="gap-2"
                >
                  {isImporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {isImporting ? "Importing..." : "Import"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {summary && (
          <Card className="border-border/40 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg">Import Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <div className="text-2xl font-bold">{safeNumber(summary.total, 0)}</div>
                  <div className="text-xs text-muted-foreground">Total Records</div>
                </div>
                <div className="p-4 rounded-lg bg-emerald-500/10 text-center">
                  <div className="text-2xl font-bold text-emerald-400">{safeNumber(summary.imported, 0)}</div>
                  <div className="text-xs text-muted-foreground">Imported</div>
                </div>
                <div className="p-4 rounded-lg bg-red-500/10 text-center">
                  <div className="text-2xl font-bold text-red-400">{safeNumber(summary.failed, 0)}</div>
                  <div className="text-xs text-muted-foreground">Failed</div>
                </div>
              </div>

              {/* Individual Results */}
              {results && results.length > 0 && (
                <div className="space-y-2">
                  {results.map((r, i) => {
                    if (!r) return null;
                    return (
                    <div
                      key={`result-${safeText(r.recordId, '')}-${i}`}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        r.status === "SUCCESS"
                          ? "border-emerald-500/30 bg-emerald-500/5"
                          : "border-red-500/30 bg-red-500/5"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {r.status === "SUCCESS" ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-400" />
                        )}
                        <div>
                          {r.status === "SUCCESS" ? (
                            <span className="text-sm font-mono">{safeText(r.recordId, '—')}</span>
                          ) : (
                            <span className="text-sm text-red-400">{safeText(r.error, 'Unknown error')}</span>
                          )}
                        </div>
                      </div>
                      {r.status === "SUCCESS" && r.instanceId && (
                        <Link href={`/instance?id=${r.instanceId}`}>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </Link>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
